/**
 * PATH: packages/ui/src/components/settings/FilesTab.tsx
 * PURPOSE: Manage file storage preferences across hubs
 *
 * WHY: Users need consistent file defaults (visibility, retention, sync)
 *      regardless of the hub they are working in.
 *
 * FLOW:
 *   ┌─────────────┐    ┌──────────────┐    ┌──────────────┐
 *   │ Load Prefs  │───▶│ Edit Defaults│───▶│ Save Updates │
 *   └─────────────┘    └──────────────┘    └──────────────┘
 *
 * DEPENDENCIES:
 *   - lucide-react: icons
 *   - SettingsApiClient: API contract for persistence
 */

import { useCallback, useEffect, useMemo, useState } from 'react'
import { Folder, Shield, Clock, Loader2, Check } from 'lucide-react'
import { cn } from '../../lib/utils'
import type { SettingsApiClient } from './types'

export interface FilePreferences {
  auto_sync: boolean
  default_visibility: 'private' | 'team' | 'organisation'
  retention_days: number
  notify_on_upload: boolean
  default_folder: string
}

export interface FilesTabProps {
  preferences?: Partial<FilePreferences>
  apiClient?: SettingsApiClient
  endpoint?: string
  isLoading?: boolean
  isSaving?: boolean
  onSave?: (prefs: FilePreferences) => Promise<void>
  className?: string
}

interface ToggleProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function Toggle({ checked, onChange, disabled }: ToggleProps) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={cn(
        'relative w-11 h-6 rounded-full transition-colors',
        checked ? 'bg-teal-500' : 'bg-slate-300 dark:bg-slate-600',
        disabled && 'opacity-50 cursor-not-allowed'
      )}
    >
      <span
        className={cn(
          'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform',
          checked && 'translate-x-5'
        )}
      />
    </button>
  )
}

export function FilesTab({
  preferences: initialPrefs,
  apiClient,
  // NOTE: apiClient baseURL already includes /api, so endpoint should NOT include /api prefix
  endpoint = '/settings/files/',
  isLoading = false,
  isSaving = false,
  onSave,
  className,
}: FilesTabProps) {
  const defaultPreferences = useMemo<FilePreferences>(() => ({
    auto_sync: true,
    default_visibility: 'team',
    retention_days: 365,
    notify_on_upload: true,
    default_folder: 'General',
    ...initialPrefs,
  }), [initialPrefs])

  const [prefs, setPrefs] = useState<FilePreferences>(defaultPreferences)
  const [remoteLoading, setRemoteLoading] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  useEffect(() => {
    if (!initialPrefs) return
    setPrefs(prev => ({ ...prev, ...initialPrefs }))
  }, [initialPrefs])

  useEffect(() => {
    if (!apiClient) return
    let isMounted = true
    setRemoteLoading(true)
    apiClient.get(endpoint)
      .then((response) => {
        if (!isMounted) return
        const data = response?.data ?? response
        const serverPrefs = data?.preferences ?? data
        if (serverPrefs) {
          setPrefs(prev => ({ ...prev, ...serverPrefs }))
          setHasChanges(false)
        }
      })
      .catch(() => {
        if (!isMounted) return
      })
      .finally(() => {
        if (!isMounted) return
        setRemoteLoading(false)
      })
    return () => {
      isMounted = false
    }
  }, [apiClient, endpoint])

  const handleChange = useCallback((key: keyof FilePreferences, value: FilePreferences[keyof FilePreferences]) => {
    setPrefs(prev => ({ ...prev, [key]: value }))
    setHasChanges(true)
  }, [])

  const handleSave = useCallback(async () => {
    if (onSave) {
      await onSave(prefs)
      setHasChanges(false)
      return
    }
    if (apiClient?.put) {
      await apiClient.put(endpoint, { preferences: prefs })
      setHasChanges(false)
    }
  }, [apiClient, endpoint, onSave, prefs])

  if (isLoading || remoteLoading) {
    return (
      <div className={cn('flex items-center justify-center py-12', className)}>
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <div className={cn('space-y-6 max-w-2xl', className)}>
      <div>
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white">Files & Storage</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Set how files are stored, shared, and retained across Quotech.
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Folder className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Default folder</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Where new uploads land by default.</p>
            </div>
          </div>
          <input
            value={prefs.default_folder}
            onChange={(e) => handleChange('default_folder', e.target.value)}
            className="w-40 px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Shield className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Default visibility</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">Who can see new uploads.</p>
            </div>
          </div>
          <select
            value={prefs.default_visibility}
            onChange={(e) => handleChange('default_visibility', e.target.value as FilePreferences['default_visibility'])}
            className="px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          >
            <option value="private">Private</option>
            <option value="team">Team</option>
            <option value="organisation">Organisation</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-start gap-3">
            <Clock className="w-5 h-5 text-slate-400 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Retention (days)</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">How long to keep archived files.</p>
            </div>
          </div>
          <input
            type="number"
            min={30}
            value={prefs.retention_days}
            onChange={(e) => handleChange('retention_days', Number(e.target.value))}
            className="w-24 px-3 py-2 rounded-lg text-sm border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-slate-900 dark:text-white"
          />
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Auto sync uploads</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Sync files to connected storage automatically.</p>
          </div>
          <Toggle checked={prefs.auto_sync} onChange={(checked) => handleChange('auto_sync', checked)} />
        </div>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">Notify on upload</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Alert you when files are added to jobs.</p>
          </div>
          <Toggle checked={prefs.notify_on_upload} onChange={(checked) => handleChange('notify_on_upload', checked)} />
        </div>
      </div>

      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          disabled={isSaving || !hasChanges}
          className="px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
        </button>
        {!isSaving && !hasChanges && (
          <span className="text-xs text-slate-400 flex items-center gap-1">
            <Check className="w-4 h-4" /> Saved
          </span>
        )}
      </div>
    </div>
  )
}

export default FilesTab
